import * as React from "react";

import {
  ActionIcon,
  Box,
  Button,
  Center,
  Divider,
  Group,
  Paper,
  Skeleton,
  Stack,
} from "@mantine/core";
import { useClickOutside, useHover, useMergedRef } from "@mantine/hooks";
import {
  IconCheckbox,
  IconGripVertical,
  IconHeading,
  IconPlus,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import { Draggable, DraggableProvided, Droppable } from "react-beautiful-dnd";

import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppState } from "../../hooks/useAppState";
import { useCountEvent } from "../../hooks/useCountEvent";
import { useCurrentEditorDocument } from "../../hooks/useEditorState";
import { newChecklistHeader, newChecklistItem } from "../../lib/generators/checklist";
import {
  ChecklistElement,
  EditableString,
  isChecklistHeader,
  isChecklistItem,
  isPageBreak,
} from "../../lib/model/checklistAppState";
import { autofocusFieldSlice, draggingIdSlice, librarySlice } from "../../lib/state/reducer";

import { ChecklistHeaderEditor } from "./ChecklistHeaderEditor";
import { ChecklistItemEditor } from "./ChecklistItemEditor";
import { InlineEditableTitle } from "./text/InlineEditableText";

export const ChecklistEditor = ({
  index,
  renderAnonymous,
  onRemove,
}: {
  index: number;
  renderAnonymous?: boolean;
  onRemove: () => void;
}): JSX.Element => {
  const reduxDispatch = useAppDispatch();
  const checklist = useCurrentEditorDocument((state) => state.contents.checklists[index], null);

  if (!checklist) {
    throw new Error("Tried to render a non-existent checklist");
  }

  if (isPageBreak(checklist)) {
    throw new Error("Tried to render checklist editor for page break");
  }

  const countEvent = useCountEvent();

  return (
    <Draggable draggableId={checklist.id} index={index}>
      {(draggableProvided, draggableSnapshot) => (
        <Paper
          withBorder
          radius={0}
          {...draggableProvided.draggableProps}
          style={{
            ...draggableProvided.draggableProps.style,
            borderColor: "#000",
            margin: draggableSnapshot.isDragging || renderAnonymous ? "10px 0" : undefined,
          }}
          ref={draggableProvided.innerRef}
        >
          <Droppable droppableId={checklist.id} type="checklistElement">
            {(droppableProvided) => (
              <Stack spacing={2}>
                <ChecklistTitle
                  checklistId={checklist.id}
                  checklistTitle={checklist.title}
                  dragHandleProps={draggableProvided.dragHandleProps}
                  onSave={(newValue) => {
                    reduxDispatch(
                      librarySlice.actions.setChecklistTitle({
                        checklistId: checklist.id,
                        newValue,
                      })
                    );
                    countEvent("checklist-title-updated");
                  }}
                  onRemove={onRemove}
                  onEmptyEnter={() => {
                    if (checklist.content.length === 0) {
                      const firstChecklistItem = newChecklistItem();
                      countEvent("empty-checklist-autoadded-item-from-title");
                      reduxDispatch(
                        librarySlice.actions.insertChecklistElement({
                          element: firstChecklistItem,
                          checklistId: checklist.id,
                        })
                      );
                      reduxDispatch(
                        autofocusFieldSlice.actions.setAutofocusField(firstChecklistItem.item.id)
                      );
                    } else {
                      if (
                        isChecklistItem(checklist.content[0]) &&
                        checklist.content[0].item.content === ""
                      ) {
                        // TODO
                        // setFocus(checklist.content[0].item.id);
                        reduxDispatch(
                          autofocusFieldSlice.actions.setAutofocusField(
                            checklist.content[0].item.id
                          )
                        );
                      }
                    }
                  }}
                />
                <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                  {renderAnonymous || draggableSnapshot.isDragging ? (
                    <Skeleton animate={false} width="100%" height="0px" />
                  ) : (
                    <Stack spacing={0}>
                      {checklist.content.map((checklistElementSnapshot, idx) => {
                        const checklistElementState = checklist.content[idx];

                        const isLast = idx === checklist.content.length - 1;
                        return (
                          <Draggable
                            key={checklistElementSnapshot.id}
                            draggableId={checklistElementSnapshot.id}
                            index={idx}
                          >
                            {(draggableElementProvided) => (
                              <Stack
                                spacing={0}
                                key={checklistElementSnapshot.id}
                                ref={draggableElementProvided.innerRef}
                                {...draggableElementProvided.draggableProps}
                              >
                                <ChecklistElementEditor
                                  checklistElement={checklistElementState}
                                  checklistId={checklist.id}
                                  dragHandleProps={draggableElementProvided.dragHandleProps}
                                  autoAdd={
                                    isLast
                                      ? () => {
                                          const nextChecklistItem = newChecklistItem();
                                          reduxDispatch(
                                            librarySlice.actions.insertChecklistElement({
                                              element: nextChecklistItem,
                                              checklistId: checklist.id,
                                            })
                                          );
                                          reduxDispatch(
                                            autofocusFieldSlice.actions.setAutofocusField(
                                              nextChecklistItem.item.id
                                            )
                                          );
                                          countEvent("checklist-item-autoadded-from-item");
                                        }
                                      : undefined
                                  }
                                />
                                {!isLast && (
                                  <InlineAdd
                                    disabled={false}
                                    onAdd={(element) => {
                                      reduxDispatch(
                                        librarySlice.actions.insertChecklistElement({
                                          element: element,
                                          checklistId: checklist.id,
                                          position: idx,
                                        })
                                      );
                                      if (isChecklistItem(element)) {
                                        reduxDispatch(
                                          autofocusFieldSlice.actions.setAutofocusField(
                                            element.item.id
                                          )
                                        );
                                        countEvent("inline-added-checklist-item");
                                      }
                                      if (isChecklistHeader(element)) {
                                        reduxDispatch(
                                          autofocusFieldSlice.actions.setAutofocusField(element.id)
                                        );
                                        countEvent("inline-added-checklist-header");
                                      }
                                    }}
                                  />
                                )}
                              </Stack>
                            )}
                          </Draggable>
                        );
                      })}
                      {droppableProvided.placeholder}
                      <Center mb={5} mt={10}>
                        <AddButtons
                          onAdd={(element) => {
                            reduxDispatch(
                              librarySlice.actions.insertChecklistElement({
                                element: element,
                                checklistId: checklist.id,
                              })
                            );
                            if (isChecklistItem(element)) {
                              reduxDispatch(
                                autofocusFieldSlice.actions.setAutofocusField(element.item.id)
                              );
                              countEvent("inline-added-checklist-item");
                            }
                            if (isChecklistHeader(element)) {
                              reduxDispatch(
                                autofocusFieldSlice.actions.setAutofocusField(element.text.id)
                              );
                              countEvent("inline-added-checklist-header");
                            }
                          }}
                        />
                      </Center>
                    </Stack>
                  )}
                </div>
              </Stack>
            )}
          </Droppable>
        </Paper>
      )}
    </Draggable>
  );
};

const ChecklistTitle = ({
  checklistId,
  checklistTitle,
  onSave,
  onEmptyEnter,
  onRemove,
  dragHandleProps,
}: {
  checklistId: string;
  checklistTitle: EditableString;
  onSave: (newValue: string) => void;
  onEmptyEnter: () => void;
  onRemove: () => void;
  dragHandleProps: DraggableProvided["dragHandleProps"];
}): JSX.Element => {
  const isDndDragging = useAppState((state) => state.isDragAndDropDragging);
  const reduxDispatch = useAppDispatch();
  const interceptDragStart = React.useCallback(() => {
    reduxDispatch(draggingIdSlice.actions.setDraggingId(checklistId));
  }, [checklistId]);
  const resetDrag = React.useCallback(() => {
    if (isDndDragging) {
      reduxDispatch(draggingIdSlice.actions.setDraggingId(null));
    }
  }, [reduxDispatch, isDndDragging]);

  return (
    <Box style={{ backgroundColor: "#000" }}>
      <Center>
        <Group spacing={0}>
          <ActionIcon
            variant="transparent"
            size="sm"
            color="gray.0"
            onMouseDown={interceptDragStart}
            onMouseUp={resetDrag}
            onTouchStart={interceptDragStart}
            onTouchEnd={resetDrag}
            {...dragHandleProps}
          >
            <IconGripVertical />
          </ActionIcon>
          <InlineEditableTitle
            text={checklistTitle}
            color="#fff"
            placeholder="Checklist Title"
            order={4}
            justify="center"
            extraPadding={20}
            onSave={onSave}
            onEmptyEnter={onEmptyEnter}
            onEmptyTab={onEmptyEnter}
          />
          <ActionIcon size="sm" onClick={() => onRemove()} variant="transparent" color="gray.0">
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      </Center>
    </Box>
  );
};

const ChecklistElementEditor = ({
  checklistElement,
  checklistId,
  dragHandleProps,
  autoAdd,
}: {
  checklistElement: ChecklistElement;
  checklistId: string;
  dragHandleProps: DraggableProvided["dragHandleProps"];
  autoAdd?: () => void;
}): JSX.Element => {
  const countEvent = useCountEvent();
  const reduxDispatch = useAppDispatch();
  const onRemove = React.useCallback(() => {
    reduxDispatch(
      librarySlice.actions.removeChecklistElement({
        checklistId: checklistId,
        checklistElement: checklistElement,
      })
    );
    countEvent(`checklist-${checklistElement.type}-removed`);
  }, [reduxDispatch]);
  if (isChecklistItem(checklistElement)) {
    return (
      <ChecklistItemEditor
        checklistItem={checklistElement}
        dragHandleProps={dragHandleProps}
        onRemove={onRemove}
        autoAdd={autoAdd}
      />
    );
  } else if (isChecklistHeader(checklistElement)) {
    return (
      <ChecklistHeaderEditor
        checklistHeaderState={checklistElement}
        dragHandleProps={dragHandleProps}
        onRemove={onRemove}
        autoAdd={autoAdd}
      />
    );
  } else {
    const _exhaustiveCheck: never = checklistElement;
    return _exhaustiveCheck;
  }
};

const InlineAdd = ({
  onAdd,
  disabled,
}: {
  onAdd: (element: ChecklistElement) => void;
  disabled?: boolean;
}): JSX.Element => {
  const { hovered, ref: hoverRef } = useHover();
  const [open, setOpen] = React.useState<boolean>(false);

  const clickOutsideRef = useClickOutside(() => setOpen(false));

  const showing = (hovered || open) && !disabled;

  const openCloseButton = React.useCallback(() => {
    setOpen(!open);
  }, [setOpen, open]);

  const ref = useMergedRef(hoverRef, clickOutsideRef);

  return (
    <Box ref={ref} my={-5} ml={-10} style={{ zIndex: 10 }}>
      <Divider
        label={
          <Group>
            <ActionIcon
              size={"xs"}
              radius="xl"
              variant="filled"
              color={open ? "gray" : "green"}
              onClick={openCloseButton}
              style={{ zIndex: 20 }}
              tabIndex={-1}
            >
              {open ? <IconX /> : <IconPlus />}
            </ActionIcon>
            {/* <Transition
              mounted={open}
              transition="slide-right"
              duration={100}
              exitDuration={100}
              timingFunction="ease"
            >
              {(styles) => ( */}
            {open && (
              <Box>
                <AddButtons
                  onAdd={(element) => {
                    setOpen(false);
                    onAdd(element);
                  }}
                />
              </Box>
            )}
            {/* )}
            </Transition> */}
          </Group>
        }
        style={{
          opacity: showing ? 1 : 0,
          zIndex: 1,
        }}
        labelPosition="left"
        my={-3}
        py={-2}
      />
    </Box>
  );
};

const AddButtons = ({ onAdd }: { onAdd: (element: ChecklistElement) => void }): JSX.Element => (
  <Group spacing={4}>
    <Button
      leftIcon={<IconCheckbox size={16} />}
      color="gray"
      variant="filled"
      size="xs"
      radius="xl"
      compact
      styles={{
        leftIcon: {
          marginRight: 5,
        },
      }}
      onClick={() => {
        onAdd(newChecklistItem());
      }}
    >
      Add Item
    </Button>
    <Button
      leftIcon={<IconHeading size={16} />}
      color="gray"
      variant="filled"
      size="xs"
      radius="xl"
      compact
      styles={{
        leftIcon: {
          marginRight: 5,
        },
      }}
      onClick={() => {
        onAdd(newChecklistHeader());
      }}
    >
      Add Header
    </Button>
  </Group>
);
